import { useContext, useState, useEffect } from 'react';
import { Search, Eye, Calendar, Target, Users, ChevronDown, Filter, X, MapPin } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import Pagination from '../../../../../../components/Pagination';
import auditPlanService from '../../../../../../services/audit_services/auditPlanService';

export default function MyAuditPlansList() {
  const { authTokens, user } = useContext(AuthContext);
  const { showError } = useToast();
  const navigate = useNavigate();

  const [auditPlans, setAuditPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [filters, setFilters] = useState({
    title: '',
    status: '',
    role: '', // 'auditor', 'auditee', or '' for all
    ordering: 'newest'
  });

  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    page_size: 10,
    total_pages: 0
  });

  const [showFiltersSection, setShowFiltersSection] = useState(false);

  useEffect(() => {
    fetchMyAuditPlans();
  }, [pagination.page, pagination.page_size]);

  const fetchMyAuditPlans = async () => {
    setLoading(true);
    
    try {
      const result = await auditPlanService.getMyAuditPlans(
        authTokens.access, 
        {
          search: filters.title,
          status: filters.status,
          ordering: filters.ordering
        }, 
        { 
          page: pagination.page, 
          page_size: pagination.page_size 
        }
      );
      
      if (result.success) {
        // Backend now provides my_role for each plan
        setAuditPlans(result.data.results);
        setPagination(prev => ({
          ...prev,
          count: result.data.count,
          total_pages: result.data.total_pages
        }));
      } else {
        console.error('Error fetching my audit plans:', result.error);
        showError(result.error || 'Error loading audit plans');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching my audit plans:', error);
      showError('Error loading audit plans');
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      title: '',
      status: '',
      role: '',
      ordering: 'newest'
    });
    setPagination(prev => ({ ...prev, page: 1 }));
    setTimeout(() => fetchMyAuditPlans(), 0);
  };

  const applyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchMyAuditPlans();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'En attente' },
      in_progress: { bg: 'bg-green-100', text: 'text-green-800', label: 'En cours' },
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Terminé' },
      closed: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Clôturé' },
      achieved: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Archivé' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      auditor: { bg: 'bg-green-100', text: 'text-green-800', label: 'Auditeur' },
      auditee: { bg: 'bg-green-100', text: 'text-green-800', label: 'Audité' }
    };

    const config = roleConfig[role] || roleConfig.auditee;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const filteredAuditPlans = auditPlans.filter(plan => {
    // Only apply role filter on frontend since backend doesn't handle it
    const matchesRole = !filters.role || plan.my_role === filters.role;
    return matchesRole;
  });

  // Apply sorting (backend handles most, but we can do role-based sorting here)
  const sortedAuditPlans = [...filteredAuditPlans];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement de vos plans d'audit...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mes plans d'audit</h1>
          <p className="text-sm text-gray-600 mt-1">Plans d'audit où vous êtes impliqué en tant qu'auditeur ou audité</p>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">Total : {sortedAuditPlans.length} audits</span>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        {/* Search Bar */}
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par titre d'audit..."
              value={filters.title}
              onChange={(e) => handleFilterChange('title', e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowFiltersSection(!showFiltersSection)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filtres
            <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${showFiltersSection ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Filters Section */}
        {showFiltersSection && (
          <div className="pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* My Role Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mon rôle</label>
                <select
                  value={filters.role}
                  onChange={(e) => handleFilterChange('role', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">Tous les rôles</option>
                  <option value="auditor">En tant qu'auditeur</option>
                  <option value="auditee">En tant qu'audité</option>
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">Tous les statuts</option>
                  <option value="pending">En attente</option>
                  <option value="in_progress">En cours</option>
                  <option value="completed">Terminé</option>
                  <option value="closed">Clôturé</option>
                  <option value="achieved">Archivé</option>
                </select>
              </div>

              {/* Program Filter - Remove this section */}

              {/* Sorting */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Trier par</label>
                <select
                  value={filters.ordering}
                  onChange={(e) => handleFilterChange('ordering', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="newest">Plus récent d'abord</option>
                  <option value="oldest">Plus ancien d'abord</option>
                  <option value="title_asc">Titre (A-Z)</option>
                  <option value="title_desc">Titre (Z-A)</option>
                </select>
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex items-center justify-end space-x-3 mt-4">
              <button
                onClick={clearFilters}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <X className="h-4 w-4 mr-2" />
                Effacer les filtres
              </button>
              <button
                onClick={applyFilters}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Appliquer les filtres
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Audit Plans Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {sortedAuditPlans.length === 0 ? (
          <div className="text-center py-12">
            <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Aucun plan d'audit trouvé</p>
            <p className="text-sm text-gray-500 mt-2">Vous n'êtes actuellement impliqué dans aucun plan d'audit</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Détails de l'audit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Programme
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mon rôle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Parties
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedAuditPlans.map((plan) => (
                  <tr key={plan.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{plan.title}</p>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{plan.objectives}</p>
                        <div className="flex items-center text-xs text-gray-500 mt-1">
                          <MapPin className="h-3 w-3 mr-1" />
                          {plan.location}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link
                        to={`/dashboard/audit/programs/${plan.program.id}`}
                        className="text-sm text-green-600 hover:text-green-900"
                      >
                        {plan.program.title}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getRoleBadge(plan.my_role)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600">
                        <div className="flex items-center mb-1">
                          <Users className="h-3 w-3 mr-1" />
                          <span className="font-medium">{plan.parties?.length || 0} parties impliquées</span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {plan.parties && plan.parties.slice(0, 2).map((party, index) => (
                            <div key={index} className="flex items-center space-x-1">
                              <span>{party.name}</span>
                              <span className={`px-1 rounded text-xs ${
                                party.partyRole === 'auditor' ? 'bg-green-100 text-green-800' :
                                party.partyRole === 'auditee' ? 'bg-green-100 text-green-800' :
                                party.partyRole === 'participant' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-purple-100 text-purple-800'
                              }`}>
                                {party.partyRole}
                              </span>
                            </div>
                          ))}
                          {plan.parties && plan.parties.length > 2 && (
                            <span className="text-gray-400">+{plan.parties.length - 2} de plus</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(plan.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        {/* View Details Button - Only action on list */}
                        <Link
                          to={`/dashboard/audit/my-plans/${plan.id}`}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50 transition-colors"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Voir les détails
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {sortedAuditPlans.length > 0 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.total_pages}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          pageSize={pagination.page_size}
          onPageSizeChange={(size) => setPagination(prev => ({ ...prev, page_size: size, page: 1 }))}
          totalItems={pagination.count}
        />
      )}
    </div>
  );
}